import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Swords, Zap, ArrowLeft, Loader2, Sparkles, Shield, Heart } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Battle({ agent, manaverse }) {
  const navigate = useNavigate();
  const [battleState, setBattleState] = useState('idle');
  const [battle, setBattle] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selectedMove, setSelectedMove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attackAnimation, setAttackAnimation] = useState(null);
  const [damageNumbers, setDamageNumbers] = useState([]);
  const [shakeTarget, setShakeTarget] = useState(null);

  const startBattle = async () => {
    setBattleState('searching');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/battle/challenge`, {
        challenger_id: agent.id,
        format: "1v1"
      });

      if (response.data.success) {
        setBattle(response.data);
        setOpponent(response.data.opponent);
        setBattleState('active');
        toast.success(`Battle started against ${response.data.opponent.name}!`);
        loadBattleStatus(response.data.battle_id);
      }
    } catch (error) {
      toast.error("Failed to start battle");
      setBattleState('idle');
    } finally {
      setLoading(false);
    }
  };

  const loadBattleStatus = async (battleId) => {
    try {
      const response = await axios.get(`${API}/battle/${battleId}`);
      setBattle(response.data.battle);
      setLogs(response.data.battle.logs || []);
      
      if (response.data.battle.status === 'completed') {
        setBattleState('ended');
      }
    } catch (error) {
      console.error('Failed to load battle status:', error);
    }
  };

  const showDamageAnimation = (damage, isPlayer) => {
    const id = Date.now();
    setDamageNumbers(prev => [...prev, { id, damage, isPlayer }]);
    setTimeout(() => {
      setDamageNumbers(prev => prev.filter(d => d.id !== id));
    }, 2000);
  };

  const executeMove = async (moveName) => {
    if (!battle || loading) return;
    
    setSelectedMove(moveName);
    setLoading(true);
    setAttackAnimation('player');

    try {
      const response = await axios.post(`${API}/battle/action`, {
        battle_id: battle.battle ? battle.battle.id : battle.id,
        agent_id: agent.id,
        move: moveName
      });

      if (response.data.success) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setShakeTarget('opponent');
        showDamageAnimation(response.data.log.damage, false);
        await new Promise(resolve => setTimeout(resolve, 300));
        setShakeTarget(null);
        setAttackAnimation(null);

        const newLogs = [response.data.log];
        if (response.data.ai_log) {
          newLogs.push(response.data.ai_log);
          await new Promise(resolve => setTimeout(resolve, 800));
          setAttackAnimation('opponent');
          await new Promise(resolve => setTimeout(resolve, 500));
          setShakeTarget('player');
          showDamageAnimation(response.data.ai_log.damage, true);
          await new Promise(resolve => setTimeout(resolve, 300));
          setShakeTarget(null);
          setAttackAnimation(null);
        }
        setLogs(prev => [...prev, ...newLogs]);

        if (response.data.battle_ended) {
          setBattleState('ended');
          const isWinner = response.data.winner === agent.id;
          toast[isWinner ? 'success' : 'error'](
            isWinner ? '🏆 Victory! You earned 300 $MANA!' : '💔 Defeat! Better luck next time.'
          );
          
          const updatedManaverse = await axios.get(`${API}/manaverse/${agent.id}`);
          localStorage.setItem('manaverse', JSON.stringify(updatedManaverse.data));
        }

        await loadBattleStatus(battle.battle ? battle.battle.id : battle.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Move failed");
      setAttackAnimation(null);
    } finally {
      setLoading(false);
      setSelectedMove(null);
    }
  };

  const getBattleData = () => {
    if (!battle) return null;
    return battle.battle || battle;
  };

  const getMoveColor = (index) => {
    const colors = [
      'from-purple-600 to-purple-800',
      'from-cyan-600 to-cyan-800',
      'from-pink-600 to-pink-800',
      'from-yellow-600 to-yellow-800'
    ];
    return colors[index % colors.length];
  };

  const renderBattleArena = () => {
    const battleData = getBattleData();
    if (!battleData || !opponent) return null;

    const myHP = battleData.challenger_id === agent.id ? battleData.challenger_hp : battleData.opponent_hp;
    const opponentHP = battleData.challenger_id === agent.id ? battleData.opponent_hp : battleData.challenger_hp;
    const myMaxHP = manaverse.stats.max_hp;
    const opponentMaxHP = opponent.stats.max_hp;

    return (
      <div className="space-y-6">
        {/* Battle Arena */}
        <div
          data-testid="battle-arena"
          className="glass-card rounded-2xl p-8 min-h-[500px] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760539619289-3047c6ce2119?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay'
          }}
        >
          {/* Particle Effects */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-purple-400 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.3, 1, 0.3],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }}
              />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-8 mb-8 relative z-10">
            {/* Your Manaverse */}
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ 
                x: attackAnimation === 'player' ? [0, 30, 0] : 0,
                opacity: 1,
                scale: attackAnimation === 'player' ? [1, 1.1, 1] : 1
              }}
              transition={{ duration: 0.5 }}
              className="space-y-3 relative"
            >
              <motion.div
                animate={shakeTarget === 'player' ? {
                  x: [0, -5, 5, -5, 5, 0],
                  rotate: [0, -2, 2, -2, 2, 0]
                } : {}}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                <div className="flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-transparent p-4 rounded-xl border border-purple-500/30">
                  <div>
                    <h3 className="font-unbounded text-xl font-bold text-white flex items-center gap-2">
                      <Shield className="text-cyan-400" size={20} />
                      {manaverse.name}
                    </h3>
                    <span className="font-rajdhani text-sm text-cyan-400">{manaverse.type}</span>
                  </div>
                  <span className="font-rajdhani text-sm bg-white/10 px-3 py-1 rounded-full text-white">
                    Lv. {Math.floor(manaverse.xp / 80) + 1}
                  </span>
                </div>
              </motion.div>
              <div>
                <div className="flex justify-between text-sm font-rajdhani text-slate-300 mb-1">
                  <span className="flex items-center gap-1">
                    <Heart size={14} className="text-red-400" />
                    HP
                  </span>
                  <span>{myHP} / {myMaxHP}</span>
                </div>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.5 }}
                >
                  <Progress 
                    value={(myHP / myMaxHP) * 100} 
                    className="h-4 bg-white/10"
                  />
                </motion.div>
              </div>
            </motion.div>

            {/* Opponent */}
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ 
                x: attackAnimation === 'opponent' ? [0, -30, 0] : 0,
                opacity: 1,
                scale: attackAnimation === 'opponent' ? [1, 1.1, 1] : 1
              }}
              transition={{ duration: 0.5 }}
              className="space-y-3 relative"
            >
              <motion.div
                animate={shakeTarget === 'opponent' ? {
                  x: [0, -5, 5, -5, 5, 0],
                  rotate: [0, -2, 2, -2, 2, 0]
                } : {}}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                <div className="flex items-center justify-between bg-gradient-to-l from-red-900/50 to-transparent p-4 rounded-xl border border-red-500/30">
                  <div>
                    <h3 className="font-unbounded text-xl font-bold text-white flex items-center gap-2">
                      <Swords className="text-red-400" size={20} />
                      {opponent.name}
                    </h3>
                    <span className="font-rajdhani text-sm text-red-400">{opponent.type}</span>
                  </div>
                  <span className="font-rajdhani text-sm bg-white/10 px-3 py-1 rounded-full text-white">
                    Lv. {Math.floor(opponent.xp / 80) + 1}
                  </span>
                </div>
              </motion.div>
              <div>
                <div className="flex justify-between text-sm font-rajdhani text-slate-300 mb-1">
                  <span className="flex items-center gap-1">
                    <Heart size={14} className="text-red-400" />
                    HP
                  </span>
                  <span>{opponentHP} / {opponentMaxHP}</span>
                </div>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.5 }}
                >
                  <Progress 
                    value={(opponentHP / opponentMaxHP) * 100} 
                    className="h-4 bg-white/10"
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Damage Numbers */}
          <AnimatePresence>
            {damageNumbers.map(({ id, damage, isPlayer }) => (
              <motion.div
                key={id}
                initial={{ opacity: 1, y: 0, scale: 0.5 }}
                animate={{ opacity: 0, y: -80, scale: 1.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2 }}
                className={`absolute ${isPlayer ? 'left-1/4' : 'right-1/4'} top-1/3 pointer-events-none z-50`}
              >
                <span className="font-unbounded text-5xl font-black text-red-500 drop-shadow-[0_0_10px_rgba(255,0,0,0.8)]">
                  -{damage}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Battle VS Icon */}
          <div className="text-center py-12 relative z-10">
            <motion.div
              animate={{ 
                rotate: 360,
                scale: [1, 1.2, 1]
              }}
              transition={{ 
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity }
              }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-cyan-600 border-4 border-white/20 shadow-[0_0_30px_rgba(112,0,255,0.6)]"
            >
              <Swords size={40} className="text-white" />
            </motion.div>
          </div>
        </div>

        {/* Move Selection */}
        {battleState === 'active' && battleData.status === 'active' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="move-selection"
          >
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <h3 className="font-rajdhani text-xl text-white mb-4 flex items-center gap-2">
                  <Sparkles className="text-yellow-400" size={20} />
                  Select Your Move
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {manaverse.moves.map((move, index) => (
                    <motion.div
                      key={move}
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        data-testid={`move-${move.replace(/\s+/g, '-').toLowerCase()}-btn`}
                        onClick={() => executeMove(move)}
                        disabled={loading}
                        className={`w-full bg-gradient-to-br ${getMoveColor(index)} hover:shadow-[0_0_20px_rgba(112,0,255,0.6)] border-2 border-white/20 text-white font-rajdhani py-8 rounded-xl transition-all relative overflow-hidden group`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                        <div className="relative flex items-center justify-center gap-2">
                          {loading && selectedMove === move ? (
                            <Loader2 className="animate-spin" size={20} />
                          ) : (
                            <Zap size={20} />
                          )}
                          <span className="text-lg font-bold">{move}</span>
                        </div>
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Battle Log */}
        <Card data-testid="battle-log" className="glass-card border-white/10">
          <CardContent className="p-6">
            <h3 className="font-rajdhani text-xl text-white mb-4">Battle Log</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
              <AnimatePresence mode="popLayout">
                {logs.map((log, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="bg-gradient-to-r from-purple-900/30 to-transparent border-l-4 border-cyan-500 rounded-lg p-3 font-inter text-sm text-slate-300"
                  >
                    <span className="font-rajdhani text-cyan-400 font-semibold">{log.attacker}</span> used{' '}
                    <span className="font-rajdhani text-yellow-400 font-semibold">{log.move}</span>!
                    <span className="mx-2">→</span>
                    <span className="text-red-400 font-bold">{log.damage} DMG</span>
                  </motion.div>
                ))}
              </AnimatePresence>
              {logs.length === 0 && (
                <p className="text-slate-400 text-center py-4 italic">Battle log will appear here...</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Battle Result */}
        {battleState === 'ended' && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="glass-card rounded-2xl p-12 text-center relative overflow-hidden"
          >
            {/* Celebration particles */}
            {battleData.winner === agent.id && (
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(50)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                    style={{
                      left: '50%',
                      top: '50%',
                    }}
                    animate={{
                      x: (Math.random() - 0.5) * 400,
                      y: (Math.random() - 0.5) * 400,
                      opacity: [1, 0],
                      scale: [1, 0]
                    }}
                    transition={{
                      duration: 1.5,
                      delay: i * 0.02
                    }}
                  />
                ))}
              </div>
            )}
            
            <motion.h2 
              className="font-unbounded text-5xl font-black mb-6 relative z-10"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 0.5, repeat: 3 }}
            >
              {battleData.winner === agent.id ? (
                <span className="text-gradient drop-shadow-[0_0_20px_rgba(255,215,0,0.6)]">🏆 VICTORY!</span>
              ) : (
                <span className="text-red-500 drop-shadow-[0_0_20px_rgba(255,0,85,0.6)]">💔 DEFEAT</span>
              )}
            </motion.h2>
            <p className="font-inter text-lg text-slate-300 mb-8 relative z-10">
              {battleData.winner === agent.id
                ? 'You earned 300 $MANA and 500 XP!'
                : 'You earned 100 XP. Train harder and come back stronger!'}
            </p>
            <div className="flex gap-4 justify-center relative z-10">
              <Button
                data-testid="battle-again-btn"
                onClick={() => {
                  setBattleState('idle');
                  setBattle(null);
                  setOpponent(null);
                  setLogs([]);
                }}
                className="bg-[#7000FF] hover:bg-[#5000CC] text-white font-rajdhani px-10 py-6 rounded-full shadow-[0_0_20px_rgba(112,0,255,0.4)] hover:shadow-[0_0_30px_rgba(112,0,255,0.6)] hover:scale-105 transition-all"
              >
                <Swords className="mr-2" size={20} />
                Battle Again
              </Button>
              <Button
                data-testid="back-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-2 border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-rajdhani px-10 py-6 rounded-full hover:scale-105 transition-all"
              >
                Back to Dashboard
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="font-unbounded text-2xl font-bold text-gradient">BATTLE ARENA</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {battleState === 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center space-y-6"
          >
            <div
              className="glass-card rounded-2xl p-12 relative overflow-hidden"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1728112265403-a4aebd5859a5?crop=entropy&cs=srgb&fm=jpg&q=85)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundBlendMode: 'overlay'
              }}
            >
              <motion.div
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Swords size={80} className="mx-auto mb-6 text-[#7000FF] drop-shadow-[0_0_20px_rgba(112,0,255,0.8)]" />
              </motion.div>
              <h2 className="font-unbounded text-4xl md:text-5xl font-bold mb-4 text-white">
                Ready to <span className="text-gradient">Battle?</span>
              </h2>
              <p className="font-inter text-lg text-slate-300 mb-8">
                Challenge a random opponent and prove your Manaverse's strength!
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  data-testid="start-battle-btn"
                  onClick={startBattle}
                  disabled={loading}
                  className="bg-[#7000FF] hover:bg-[#5000CC] text-white font-bold py-8 px-14 rounded-full text-lg shadow-[0_0_30px_rgba(112,0,255,0.6)] hover:shadow-[0_0_40px_rgba(112,0,255,0.8)] transition-all duration-300"
                >
                  {loading ? (
                    <Loader2 className="animate-spin mr-2" size={24} />
                  ) : (
                    <Swords className="mr-2" size={24} />
                  )}
                  Find Opponent
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {battleState === 'searching' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-2xl mx-auto text-center py-20"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Loader2 size={64} className="mx-auto mb-6 text-[#7000FF]" />
            </motion.div>
            <p className="font-rajdhani text-3xl text-white">Finding opponent...</p>
          </motion.div>
        )}

        {(battleState === 'active' || battleState === 'ended') && renderBattleArena()}
      </div>
    </div>
  );
}